import React from 'react';
import c from 'classnames';
import { useTranslation } from 'react-i18next';
import { useFlag } from '@wix/da-react-context/pkg/flags/hooks';
import { BiData, PureBiData } from '@wix/da-bi/pkg/events';
import { ReferralType, BiEventType } from '@wix/da-bi/pkg/constants';
import StandardMenu from '../../../../Dropdown/MenuPreset/StandardMenu';
import { getDeviationResourceType } from '../../../../DeviationViews/_helpers';
import { useDeviationAttributes } from '../../../../DeviationViews/hooks/useDeviationAttributes';
import { useRestrictToLoggedIn } from '../../../../utils/hooks/useRestrictToLoggedIn';
import { useFreeDownloadAction, usePremiumDownloadAction } from '../../DownloadButton/hooks';
import { getAllowOffers, getHasBuyItNow } from '../../../helpers';
import s from './MoreMenuContent.scss';
// This is used only on desktop
const MoreMenuContent = ({ deviation , isOwner , closeTooltip =()=>null , showReportModal , showDesktopDeviationSharePopup , showNftLinkPopup , shareDeviationToPost , showAddToGroupPopup , showAwardBadgePopup , onToggleCommentPanel  })=>{
    var _deviation_author;
    const kill_prints_act_two = useFlag('kill_prints_act_two');
    const isAdopts = useFlag('adoptables');
    const { t  } = useTranslation();
    const { restrictToLoggedIn  } = useRestrictToLoggedIn();
    const { isPrintPurchasable , isShareable , isReportable , hasNft , isCommentable , isCoreBadgeAwardable  } = useDeviationAttributes(deviation, isOwner);
    const adoptableOffers = getAllowOffers(deviation) && isAdopts;
    const isAnAdoptable = deviation.isAdoptable && isAdopts;
    const adoptableBuyItNow = getHasBuyItNow(deviation) && isAdopts;
    const overflowByDefault = isAdopts && isAnAdoptable && adoptableOffers && adoptableBuyItNow; // when two buttons are shown, overflow actions end up here
    const overflowByResponsiveDesign = isAdopts && isAnAdoptable && !overflowByDefault; // show these buttons when in lower resolutions
    const { print: { lowestPrice , purchaseUrl  } = {}  } = deviation;
    const isIncludingPrintPurchase = isPrintPurchasable && !!purchaseUrl && !kill_prints_act_two;
    const printLinkLabel = lowestPrice ? t('actionbar.buyPrintsFor', {
        price: lowestPrice
    }) : t('actionbar.buyPrints');
    const freeDownloadAction = useFreeDownloadAction(deviation, isOwner);
    const premiumDownloadAction = usePremiumDownloadAction(deviation, isOwner);
    var _deviation_author_useridUuid;
    return /*#__PURE__*/ React.createElement(StandardMenu, {
        className: s['menu'],
        id: "actionbar-more-menu",
        items: [
            isAnAdoptable && isCommentable && {
                className: c(s['overflow-responsive']),
                value: 'comment',
                label: t('actionbar.comment.label'),
                onClick: onToggleCommentPanel,
                biData: BiData({
                    evid: 105,
                    typeid: getDeviationResourceType(deviation),
                    itemid: deviation.deviationId,
                    creatorid: (_deviation_author_useridUuid = (_deviation_author = deviation.author) === null || _deviation_author === void 0 ? void 0 : _deviation_author.useridUuid) !== null && _deviation_author_useridUuid !== void 0 ? _deviation_author_useridUuid : ''
                })
            },
            isAnAdoptable && premiumDownloadAction.hasAction && {
                className: c(overflowByDefault && s['overflowed'], overflowByResponsiveDesign && s['overflow-responsive']),
                value: 'download',
                label: t('actionbar.premiumDownload'),
                link: premiumDownloadAction.href,
                onClick: ()=>{
                    premiumDownloadAction.onClick && premiumDownloadAction.onClick();
                    closeTooltip();
                },
                biData: PureBiData({
                    evid: 350,
                    ispaiddownload: 1,
                    deviationid: deviation.deviationId
                })
            },
            isAnAdoptable && freeDownloadAction.hasAction && {
                className: c(overflowByDefault && s['overflowed'], overflowByResponsiveDesign && s['overflow-responsive']),
                value: 'download',
                label: t('actionbar.freeDownload'),
                link: freeDownloadAction.href,
                onClick: ()=>{
                    freeDownloadAction.onClick && freeDownloadAction.onClick();
                    closeTooltip();
                },
                biData: PureBiData({
                    evid: 350,
                    ispaiddownload: 0,
                    deviationid: deviation.deviationId
                })
            },
            !!showAddToGroupPopup && {
                value: 'addToGroup',
                label: t('actionbar.addToGroup'),
                className: c(overflowByDefault && s['overflowed'], overflowByResponsiveDesign && s['overflow-responsive']),
                onClick: ()=>{
                    showAddToGroupPopup();
                    closeTooltip();
                },
                biData: BiData({
                    evid: 340,
                    deviationid: deviation.deviationId,
                    sectionname: 'add_to_group'
                })
            },
            isAnAdoptable && isCoreBadgeAwardable && {
                value: 'awardBadge',
                label: t('actionbar.awardBadge'),
                className: c(overflowByDefault && s['overflowed'], overflowByResponsiveDesign && s['overflow-responsive']),
                onClick: ()=>{
                    showAwardBadgePopup(deviation);
                    closeTooltip();
                },
                biData: {
                    evid: BiEventType.GIVE_BADGE_AWARD,
                    sectionname: 'deviation',
                    typeid: deviation.typeId,
                    itemid: deviation.deviationId
                }
            },
            isShareable && {
                value: 'share',
                label: t('actionbar.share'),
                biData: BiData({
                    evid: 355,
                    typeid: getDeviationResourceType(deviation),
                    itemid: deviation.deviationId,
                    deviationid: deviation.deviationId
                }),
                onClick: showDesktopDeviationSharePopup
            },
            isShareable && {
                value: 'shareToPost',
                label: t('actionbar.shareToPost'),
                onClick: restrictToLoggedIn(ReferralType.SHARE_TO_POST, ()=>shareDeviationToPost(deviation)),
                biData: BiData({
                    evid: 356,
                    typeid: getDeviationResourceType(deviation),
                    itemid: deviation.deviationId,
                    social_share: 'da_post',
                    deviationid: deviation.deviationId
                })
            },
            isIncludingPrintPurchase && {
                value: 'printPurchase',
                label: printLinkLabel,
                biData: {
                    evid: BiEventType.BUY_CLICK,
                    deviationid: deviation.deviationId
                },
                link: purchaseUrl
            },
            isReportable && {
                value: 'report',
                label: t('actionbar.report'),
                biData: BiData({
                    evid: 20,
                    modal_version: 2,
                    deviationid: deviation.deviationId
                }),
                onClick: ()=>{
                    showReportModal(deviation);
                    closeTooltip();
                }
            },
            !hasNft && isOwner && {
                value: 'linknft',
                label: t('actionbar.linkNft'),
                onClick: ()=>{
                    showNftLinkPopup(deviation);
                    closeTooltip();
                }
            }
        ]
    });
};
MoreMenuContent.displayName = 'MoreMenuContent';
export default MoreMenuContent;
